import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

export interface PhotoGridTileProps extends Omit<BoxProps, 'onClick' | 'children'> {
  src: string
  alt?: string
  /** e.g. "014" */
  index?: React.ReactNode
  liked?: boolean
  likeCount?: number
  /** Pinned-comment count shown as a top-right glass badge, e.g. "2 PINS" */
  pinLabel?: React.ReactNode
  /** True when this photo is the album's current cover — shows a persistent badge. */
  isCover?: boolean
  /** Owner-only "set as cover" affordance — omit to hide entirely (e.g. for guests). */
  onSetCover?: () => void
  onClick?: () => void
  onLikeToggle?: () => void
  /**
   * Owner-only "delete this photo" affordance — omit to hide entirely (e.g.
   * for guests). Previously the only way to delete an uploaded photo was to
   * open the Lightbox first (see #28 feedback: "remove option is not here").
   */
  onDelete?: () => void
}

const Root = styled(Box)({
  position: 'relative',
  breakInside: 'avoid',
  borderRadius: 14,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  cursor: 'pointer',
  transition: 'box-shadow var(--ds-duration-normal) var(--ds-ease)',
  '&:hover': {
    boxShadow: 'var(--ds-shadow-lift)',
  },
  '&:hover .pgt-img': {
    transform: 'scale(1.025)',
  },
  '&:hover .pgt-foot': {
    opacity: 1,
  },
  '&:hover .pgt-set-cover': {
    opacity: 1,
  },
})

const Img = styled('img')({
  width: '100%',
  display: 'block',
  transition: 'transform var(--ds-duration-slow) var(--ds-ease-out)',
})

const Foot = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '34px 13px 11px',
  background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.6))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  opacity: 0,
  transition: 'opacity var(--ds-duration-normal) var(--ds-ease)',
})

const IndexText = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.1em',
  color: 'rgba(255,255,255,0.75)',
})

const FootRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
})

const DeleteBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(255,255,255,0.92)',
  color: 'var(--ds-ink)',
  cursor: 'pointer',
  '& svg': { fontSize: 15 },
  '&:hover': { background: 'var(--ds-danger, #e5484d)', color: '#fff' },
})

const LikePill = styled('button', {
  shouldForwardProp: (p) => p !== '$liked',
})<{ $liked?: boolean }>(({ $liked }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 26,
  padding: '0 10px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(255,255,255,0.92)',
  fontSize: 11,
  fontWeight: 650,
  color: $liked ? 'var(--ds-accent-deep-a, #c2452d)' : 'var(--ds-ink)',
  cursor: 'pointer',
}))

const PinBadge = styled(Box)({
  position: 'absolute',
  top: 10,
  right: 10,
  height: 22,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '0 9px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(13,13,15,0.6)',
  backdropFilter: 'var(--ds-blur-sm, blur(8px))',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.08em',
  color: '#ffcf7d',
})

const cornerBadgeSx = {
  position: 'absolute' as const,
  top: 10,
  left: 10,
  zIndex: 1,
  height: 22,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '0 9px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(13,13,15,0.6)',
  backdropFilter: 'var(--ds-blur-sm, blur(8px))',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.08em',
  '& svg': { fontSize: 12 },
}

const CoverBadge = styled(Box)({
  ...cornerBadgeSx,
  color: '#ffcf7d',
})

const SetCoverButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  ...cornerBadgeSx,
  color: 'rgba(255,255,255,0.85)',
  cursor: 'pointer',
  opacity: 0,
  transition: 'opacity var(--ds-duration-normal) var(--ds-ease)',
  '&:hover': { color: '#ffcf7d' },
})

/** Masonry-grid photo cell with a hover-reveal index/like footer and an optional pin-count badge. */
export function PhotoGridTile({
  src,
  alt = '',
  index,
  liked,
  likeCount,
  pinLabel,
  isCover,
  onSetCover,
  onClick,
  onLikeToggle,
  onDelete,
  ...rest
}: PhotoGridTileProps) {
  return (
    <Root onClick={onClick} {...rest}>
      {pinLabel && <PinBadge>{pinLabel}</PinBadge>}
      {isCover ? (
        <CoverBadge>
          <StarIcon /> COVER
        </CoverBadge>
      ) : (
        onSetCover && (
          <SetCoverButton
            type='button'
            className='pgt-set-cover'
            aria-label='Set as album cover'
            onClick={(e) => {
              e.stopPropagation()
              onSetCover()
            }}
          >
            <StarBorderIcon /> SET COVER
          </SetCoverButton>
        )
      )}
      <Img className='pgt-img' src={src} alt={alt} loading='lazy' />
      <Foot className='pgt-foot'>
        {index != null && <IndexText>{index}</IndexText>}
        <FootRight>
          {likeCount != null && (
            <LikePill
              type='button'
              $liked={liked}
              onClick={(e) => {
                e.stopPropagation()
                onLikeToggle?.()
              }}
            >
              ❤ {likeCount}
            </LikePill>
          )}
          {onDelete && (
            <DeleteBtn
              type='button'
              aria-label='Delete photo'
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            >
              <DeleteOutlineIcon />
            </DeleteBtn>
          )}
        </FootRight>
      </Foot>
    </Root>
  )
}
